import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { X, Check, Search, User, ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface TeacherSelectorProps {
  teachers: any[];
  selectedTeacherIds: string[];
  onTeacherIdsChange: (teacherIds: string[]) => void;
  onClose: () => void;
}

export const TeacherSelector: React.FC<TeacherSelectorProps> = ({
  teachers,
  selectedTeacherIds,
  onTeacherIdsChange,
  onClose,
}) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = React.useState('');

  const filteredTeachers = teachers.filter(teacher =>
    `${teacher.firstname} ${teacher.lastname}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleTeacherSelection = (teacherId: string) => {
    const newIds = selectedTeacherIds.includes(teacherId)
      ? selectedTeacherIds.filter(id => id !== teacherId)
      : [...selectedTeacherIds, teacherId];
    onTeacherIdsChange(newIds);
  };

  return (
    <div className="p-6 w-auto min-h-[38vh]">
      <div className="flex justify-start items-center mb-4">
        <User className="mr-2" />
        <p className="text-lg font-semibold">{t('agenda.selectTeachers')}</p>
      </div>
      <div className="grid gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('agenda.searchStudent')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 w-48"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto border rounded-md">
          {filteredTeachers.map(teacher => {
            const isSelected = selectedTeacherIds.includes(teacher.id);
            return (
              <div
                key={teacher.id}
                className={`flex items-center p-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-100 ${
                  isSelected ? 'bg-blue-50' : ''
                }`}
                onClick={() => toggleTeacherSelection(teacher.id)}
              >
                <div className={`h-4 w-4 border rounded-sm mr-2 flex items-center justify-center ${
                  isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                }`}>
                  {isSelected && <Check className="h-3 w-3 text-white" />}
                </div>
                <span className="flex-1">{teacher.firstname} {teacher.lastname}</span>
              </div>
            );
          })}
          {filteredTeachers.length === 0 && (
            <div className="p-4 text-center text-muted-foreground">
              {t('agenda.noTeacherFound')}
            </div>
          )}
        </div>
        {filteredTeachers.length > 0 && (
          <div className="flex justify-end items-center space-x-2 mt-4">
            <Button
              onClick={onClose}
              disabled={selectedTeacherIds.length === 0}
            >
              {t('common.done')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
